import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ApiError } from '../../api/client';
import { campaignsApi, type Campaign } from '../../api/campaigns';
import { Alert, Card, ErrorState, LoadingState, PageHeader, PageLayout, Section, Tabs } from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { ContactsTab } from './ContactsTab';
import { MembersTab } from './MembersTab';
import { OverviewTab } from './OverviewTab';
import { PhoneTab } from './PhoneTab';
import { SettingsTab } from './SettingsTab';

/**
 * Eine Kampagne mit ihren Reitern (02_navigation_ux_tutorial_i18n.md).
 *
 * Statistik und Aufnahmen sind in dieser Phase bewusst Platzhalter - sie
 * entstehen in den Phasen 7 und 9.
 */
const TABS = ['overview', 'phone', 'contacts', 'callers', 'statistics', 'recordings', 'settings'] as const;
type Tab = (typeof TABS)[number];

const ADMIN_ONLY: Tab[] = ['callers', 'settings'];

export function CampaignPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();
  const { campaignId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;
  const requested = (params.get('tab') ?? 'overview') as Tab;
  const tab: Tab = TABS.includes(requested) && (isAdmin || !ADMIN_ONLY.includes(requested)) ? requested : 'overview';

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await campaignsApi.get(campaignId);
      setCampaign(response.campaign);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [campaignId, handleApiError, tError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <PageLayout width="mittel">
        <ErrorState
          message={error}
          action={
            <Link className="btn btn-secondary" to="/kampagnen">
              {t('action.back')}
            </Link>
          }
        />
      </PageLayout>
    );
  }

  if (!campaign) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  const visibleTabs = TABS.filter((entry) => isAdmin || !ADMIN_ONLY.includes(entry));

  return (
    <PageLayout>
      <PageHeader
        title={campaign.name}
        description={campaign.description ?? undefined}
        actions={
          <Link className="btn btn-secondary" to="/kampagnen">
            {t('action.back')}
          </Link>
        }
      />

      <Tabs
        label={t('campaigns.title')}
        active={tab}
        onChange={(entry) => setParams({ tab: entry })}
        tabs={visibleTabs.map((entry) => ({
          id: entry,
          label: t(`campaign.tab.${entry}` as TranslationKey),
        }))}
      />

      {tab === 'overview' ? <OverviewTab campaign={campaign} onChange={load} /> : null}
      {tab === 'phone' ? <PhoneTab campaign={campaign} /> : null}
      {tab === 'contacts' ? <ContactsTab campaign={campaign} /> : null}
      {tab === 'callers' ? <MembersTab campaign={campaign} /> : null}
      {tab === 'settings' ? <SettingsTab campaign={campaign} onChange={load} /> : null}
      {tab === 'statistics' || tab === 'recordings' ? (
        <Card>
          <Section title={t('soon.title')}>
            <Alert tone="info">
              {tab === 'statistics' ? t('soon.campaignStatistics') : t('soon.campaignRecordings')}
            </Alert>
            <p className="lead">{t('soon.hint')}</p>
          </Section>
        </Card>
      ) : null}
    </PageLayout>
  );
}
